import { Tab, TabList, TabPanel, TabPanels, Tabs } from "@reach/tabs";
import _clone from "lodash/clone";
import * as React from "react";
import styled, { css } from "styled-components";
import { Button } from "../Button";
import { useScrollbarSize } from "../hooks/useScrollbarSize";
import { Icon } from "../Icon";
import { mediaQueries, style } from "../style";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { focusOutline } from "../utils/focusOutlineStyle";
import { WizardStepProps } from "./WizardStep";
import { ContinueButton, ControlsContainer, SkipButton } from "./WizardStepControls";

const SIDEBAR_WIDTH = 200;
const BODY_MAX_WIDTH = 1200;
const BODY_HORIZONTAL_PADDING = 30;
const BODY_HORIZONTAL_PADDING_MOBILE = 16;

const StepsContainer = styled(Tabs)`
  position: relative;
`;

const StepList = styled(TabList)``;
const Step = styled(Tab)``;
const StepPanel = styled(TabPanel)``;
const StepPanels = styled(TabPanels)``;

const MobileProgressBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 2px;
  width: 40%;
  background-color: ${style.colors.blue.dark};
  transition: ${style.vars.transitionSpeed.base} width;
`;

const MobileStepControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MobileStepControl = styled(Button)``;

const MobileStepCounter = styled(Text).attrs({ type: "small" })`
  margin: ${style.vars.whitespace.xsmall} ${style.vars.whitespace.small} 0;
  text-align: center;
`;

const MobileStepLabel = styled(Text).attrs({ type: "h4" })`
  margin-top: 0;
  text-align: center;
`;

const Close = styled(Button).attrs({ variation: "tertiary" })`
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  float: right;
  z-index: ${style.vars.zIndex.base};
  font-size: ${style.vars.fontSize.base};
`;

const DesktopStepNumber = styled.span`
  flex-shrink: 0;
  position: relative;
  top: calc(50% - 15px);
  left: 0;
  display: inline-block;
  width: 30px;
  height: 30px;
  line-height: 28px;
  text-align: center;
  border: ${style.vars.borderWidth.thin} solid ${style.colors.grey.light};
  border-radius: 50%;
  margin-right: 27px;
`;

const DesktopStepNumberChecked = styled(DesktopStepNumber)`
  border: 1px solid ${style.colors.green.primary};
  color: ${style.colors.green.primary};
`;

type WizardContainerProps = {
  scrollbarSize: number;
  hideBackground: boolean;
};

const WizardContainer = styled.div<WizardContainerProps>`
  position: relative;
  counter-reset: overview-counter;
  padding: ${style.vars.whitespace.regular} ${BODY_HORIZONTAL_PADDING_MOBILE}px;
  margin-right: -${BODY_HORIZONTAL_PADDING_MOBILE}px;
  margin-left: -${BODY_HORIZONTAL_PADDING_MOBILE}px;

  /* Adapted from https://github.internal.digitalocean.com/digitalocean/aurora/blob/2df34a8ae9c647d6f860e70b55c446390a963a7f/app/styles/components/_overview-wizard.scss#L21-L34 */

  ${({ hideBackground }) =>
    !hideBackground &&
    css<WizardContainerProps>`
      &::before {
        content: "";
        position: absolute;
        top: 0;
        bottom: 0;
        left: -${BODY_HORIZONTAL_PADDING}px;
        right: -${BODY_HORIZONTAL_PADDING}px;
        min-width: calc(100% + 50vw);
        background: ${style.colors.grey.lightest};
        z-index: -1;

        ${mediaQueries.xlargeMin} {
          left: -50vw;
          /**
   * This doesn't seem to be necessary in Aurora for some reason, but here we
   * need to take the size of the scrollbar into account (for Windows users
   * and Mac users that have permanent scrollbars enabled) to ensure that the
   * element doesn't cause a horizontal scrollbar to appear.
   */
          right: calc(
            -50vw + (${BODY_MAX_WIDTH / 2}px) + (${SIDEBAR_WIDTH / 2}px) + (${({ scrollbarSize }) => scrollbarSize / 2}px) + ${BODY_HORIZONTAL_PADDING}px
          );
        }
      }
    `}

  ${mediaQueries.xlargeMin} {
    max-width: calc(${BODY_MAX_WIDTH}px + (${BODY_HORIZONTAL_PADDING}px * 2));
    padding: ${style.vars.space["4"]} ${BODY_HORIZONTAL_PADDING}px ${style.vars.whitespace.large};
    margin-right: -${BODY_HORIZONTAL_PADDING}px;
    margin-left: -${BODY_HORIZONTAL_PADDING}px;
  }

  ${StepsContainer} {
    background: ${style.colors.white};

    ${mediaQueries.xlargeMin} {
      display: grid;
      grid-template-columns: 1fr 2fr;
      padding: ${style.vars.whitespace.large};
      border: ${style.vars.borderWidth.thin} solid ${style.colors.shadesOfGrey["4"]};
      border-radius: ${style.vars.borderRadius.large};
    }
  }

  ${StepList} {
    flex-direction: column;
    background-color: ${style.colors.white};
    padding: ${style.vars.whitespace.small} ${style.vars.whitespace.regular} 0;
    border-top: ${style.vars.borderWidth.base} solid ${style.colors.grey.light};
    border-bottom: ${style.vars.borderWidth.thin} solid ${style.colors.grey.light};

    ${mediaQueries.xlargeMin} {
      padding: 0 ${style.vars.whitespace.regular} 0 0;
      border-top: 0;
      border-right: ${style.vars.borderWidth.thin} solid ${style.colors.grey.light};
      border-bottom: 0;
    }
  }

  ${Step} {
    display: flex;
    align-items: baseline;
    align-self: flex-start;
    text-align: left;
    color: ${style.colors.grey.dark};
    padding: 0;
    margin-bottom: 25px;
    font-weight: ${style.vars.fontWeight.bold};
    border: 0;
    &[data-selected] {
      color: ${style.colors.grey.darkest};
      background-color: ${style.colors.white};
      ${DesktopStepNumber} {
        color: ${style.colors.blue.dark};
        box-shadow: ${style.colors.blue.dark} 0 0 0 1px;
        border-color: ${style.colors.blue.dark};
      }
      ${DesktopStepNumberChecked} {
        color: ${style.colors.green.primary};
        box-shadow: ${style.colors.green.primary} 0 0 0 1px;
        border-color: ${style.colors.green.primary};
      }
    }
    &:hover,
    &:active {
      color: ${style.colors.grey.darkest};
      background-color: ${style.colors.white};
      ${DesktopStepNumber} {
        border: 1px solid ${style.colors.grey.primary};
      }
      ${DesktopStepNumberChecked} {
        border: 1px solid ${style.colors.green.dark};
        color: ${style.colors.green.dark};
      }
      &[data-selected] {
        ${DesktopStepNumber} {
          box-shadow: ${style.colors.blue.darkest} 0 0 0 1px;
          border-color: ${style.colors.blue.darkest};
          color: ${style.colors.blue.darkest};
        }
        ${DesktopStepNumberChecked} {
          box-shadow: ${style.colors.green.dark} 0 0 0 1px;
          border-color: ${style.colors.green.dark};
          color: ${style.colors.green.dark};
        }
      }
    }
    &:focus {
      ${focusOutline};
      color: ${style.colors.grey.darkest};
      ${DesktopStepNumber} {
        box-shadow: ${style.colors.blue.darkest} 0 0 0 1px;
        border-color: ${style.colors.blue.darkest};
        color: ${style.colors.blue.darkest};
      }
      ${DesktopStepNumberChecked} {
        box-shadow: ${style.colors.green.primary} 0 0 0 1px;
        border-color: ${style.colors.green.primary};
        color: ${style.colors.green.primary};
      }
      &[data-selected] {
        ${DesktopStepNumberChecked} {
          box-shadow: ${style.colors.green.dark} 0 0 0 1px;
          border-color: ${style.colors.green.dark};
          color: ${style.colors.green.dark};
        }
      }
    }

    &[disabled][data-selected] {
      opacity: 1;
    }

    &[disabled]:not([data-selected]) {
      opacity: 1;

      &:hover {
        color: ${style.colors.grey.dark};

        ${DesktopStepNumber} {
          border-color: ${style.colors.grey.light};
        }

        ${DesktopStepNumberChecked} {
          border-color: ${style.colors.green.primary};
        }
      }
    }
  }

  ${StepPanels} {
    padding-top: ${style.vars.whitespace.regular};
    overflow-y: auto;
    overflow-x: hidden;
  }

  ${StepPanel} {
    padding: ${style.vars.whitespace.regular};

    ${mediaQueries.xlargeMin} {
      padding: 0 0 0 40px;
    }
  }
`;

const TitleBox = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: baseline;
`;

const DesktopStepLabel = styled.span<{ strikethrough: boolean }>`
  ${({ strikethrough }) =>
    strikethrough &&
    css`
      text-decoration: line-through;
    `};
`;

export type CheckedSteps = {
  [key: string]: boolean;
};

type HideCloseProp = boolean | { hide: boolean; text?: string };
type ShowCloseButtonData = { show: false } | { show: true; text: string };

const normalizeHideClose = (hideClose: HideCloseProp): ShowCloseButtonData => {
  if (hideClose === true) {
    return { show: false };
  }

  if (hideClose === false) {
    return { show: true, text: "Hide" };
  }

  if (hideClose.hide === true) {
    return { show: false };
  }

  return {
    show: true,
    text: hideClose.text ?? "Hide",
  };
};

export type WizardProps = {
  title?: string;
  firstStepAlwaysChecked?: boolean;
  setIsHidden: (newValue: boolean) => void;
  checkedSteps: CheckedSteps;
  setCheckedSteps: React.Dispatch<CheckedSteps>;
  tabIndex: number;
  setTabIndex: React.Dispatch<number>;
  className?: string;
  hideClose?: HideCloseProp;
  forceStepOrder?: boolean;
  hideBackground?: boolean;
  closeTooltip?: string;
  onClose?: (isCompleted?: boolean) => void;
};

export const Wizard: React.FC<WizardProps> = ({
  title = "",
  firstStepAlwaysChecked = true,
  setIsHidden,
  checkedSteps,
  setCheckedSteps,
  tabIndex,
  setTabIndex,
  className,
  children,
  hideClose = false,
  forceStepOrder,
  hideBackground = false,
  closeTooltip,
  onClose,
}) => {
  const showCloseButton = normalizeHideClose(hideClose);

  const isSmallerThanXLarge = mediaQueries.largeMax.useMatchesBreakpoint();
  const { horizontalScrollbarSize } = useScrollbarSize();

  const stepsArray = React.Children.toArray(children);
  const currentStep = stepsArray[tabIndex];

  function handleTabChange(index: number): void {
    setTabIndex(index);
  }

  const handleOnClose = (isCompleted?: boolean): void => {
    setIsHidden(true);
    onClose?.(isCompleted);
  };

  async function handleControls(index: number, stepId?: string, callback?: (index: number) => any) {
    if (callback) {
      await callback(index);
    }

    if (stepId) {
      const newCheckedSteps = _clone(checkedSteps);
      newCheckedSteps[stepId] = true;
      setCheckedSteps(newCheckedSteps);
    }

    if (index + 1 === React.Children.count(children)) {
      handleOnClose(true);
    } else {
      handleTabChange(index + 1);
    }
  }

  return (
    <WizardContainer className={className} scrollbarSize={horizontalScrollbarSize} hideBackground={hideBackground}>
      <TitleBox>
        <Text type={isSmallerThanXLarge ? "h4" : "h3"}>{title}</Text>
        {showCloseButton.show &&
          (closeTooltip ? (
            <Tooltip content={closeTooltip} placement="left" multiple>
              <Close onClick={() => handleOnClose()}>{showCloseButton.text}</Close>
            </Tooltip>
          ) : (
            <Close onClick={() => handleOnClose()}>{showCloseButton.text}</Close>
          ))}
      </TitleBox>

      <StepsContainer
        orientation={isSmallerThanXLarge ? "horizontal" : "vertical"}
        index={tabIndex}
        onChange={
          forceStepOrder
            ? () => {
                /*noop*/
              }
            : handleTabChange
        }
      >
        <StepList>
          {isSmallerThanXLarge ? (
            <>
              {forceStepOrder ? (
                <MobileStepCounter>
                  {tabIndex + 1} of {stepsArray.length}
                </MobileStepCounter>
              ) : (
                <MobileStepControls>
                  <MobileStepControl
                    variation="icon"
                    icon="arrow-left-long"
                    disabled={tabIndex === 0}
                    onClick={() => handleTabChange(tabIndex - 1)}
                  />
                  <MobileStepCounter>
                    {tabIndex + 1} of {stepsArray.length}
                  </MobileStepCounter>
                  <MobileStepControl
                    variation="icon"
                    icon="arrow-right-long"
                    disabled={tabIndex === stepsArray.length - 1}
                    onClick={() => handleTabChange(tabIndex + 1)}
                  />
                </MobileStepControls>
              )}
              <MobileStepLabel>{React.isValidElement(currentStep) && currentStep.props.label}</MobileStepLabel>
            </>
          ) : (
            React.Children.map(children, (step, index) => {
              if (!React.isValidElement(step)) {
                return step;
              }

              const { id, label } = step.props as WizardStepProps;
              // eslint-disable-next-line @typescript-eslint/naming-convention
              const stepChecked = (firstStepAlwaysChecked && index === 0) || (checkedSteps[id] && checkedSteps[id] === true);

              return (
                <Step disabled={forceStepOrder}>
                  {stepChecked ? (
                    <DesktopStepNumberChecked>
                      <Icon icon="check" />
                    </DesktopStepNumberChecked>
                  ) : (
                    <DesktopStepNumber>{index + 1}</DesktopStepNumber>
                  )}
                  <DesktopStepLabel strikethrough={stepChecked && index !== tabIndex}>{label}</DesktopStepLabel>
                </Step>
              );
            })
          )}
        </StepList>
        <StepPanels>
          {React.Children.map(children, (step, index) => {
            if (!React.isValidElement(step)) {
              return step;
            }

            const {
              id,
              // eslint-disable-next-line @typescript-eslint/naming-convention
              controls = true,
              controlsCallback,
              isCheckable,
              skipLabel = "Skip",
              children: childrenOfStep,
            } = step.props as WizardStepProps;
            let { continueLabel } = step.props;

            if (controls && !continueLabel) {
              if (index === React.Children.count(children) - 1) {
                continueLabel = `Great, I'm done`;
              } else if (index === 0) {
                continueLabel = "Get Started";
              } else {
                continueLabel = "Continue";
              }
            }

            return (
              <StepPanel key={`${title}-panel-${index}`}>
                {childrenOfStep}
                {controls && (
                  <ControlsContainer>
                    <ContinueButton onClick={() => handleControls(index, isCheckable ? id : undefined, controlsCallback)}>
                      {continueLabel}
                    </ContinueButton>
                    {isCheckable && skipLabel && <SkipButton onClick={() => handleControls(index)}>{skipLabel}</SkipButton>}
                  </ControlsContainer>
                )}
              </StepPanel>
            );
          })}
        </StepPanels>
        {isSmallerThanXLarge && (
          <MobileProgressBar
            style={{
              // The first step is always "completed", so we start at 1 here instead of 0.
              width: `${(Math.max(tabIndex, 1) / stepsArray.length) * 100}%`,
            }}
          />
        )}
      </StepsContainer>
    </WizardContainer>
  );
};
